import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Tag,
  DatePicker,
  Select,
  Tooltip,
  Space,
  Table,
  Button,
  Input,
  Typography,
  Avatar,
  Empty,
} from "antd";
import { useParams, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { getSentEmailsPaginated } from "@api/email/emailCampaign";
import {
  ReloadOutlined,
  FilterOutlined,
  EyeOutlined,
  LinkOutlined,
  MailOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DesktopOutlined,
  MobileOutlined,
  InfoCircleOutlined,
  CalendarOutlined,
  GlobalOutlined,
  WarningOutlined,
  PieChartOutlined,
  LineChartOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { debounce } from "lodash";
import { SentEmailsDomainCard, domainColors } from "./components";
import { format } from "date-fns";
import "../../../../assets/css/sentEmailsPage.css";

const { Search } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text } = Typography;

const EmailCampaignSentEmails = () => {
  const { id } = useParams();
  const history = useHistory();
  const { user } = useSelector((state) => state.auth);

  // State
  const [loading, setLoading] = useState(true);
  const [sentEmails, setSentEmails] = useState([]);
  const [sentEmailsStats, setSentEmailsStats] = useState(null);
  const [domainStats, setDomainStats] = useState(null);
  const [campaignName, setCampaignName] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    status: undefined,
    dateRange: undefined,
    searchText: "",
    domain: undefined,
  });

  // Fetch sent emails
  const fetchSentEmails = useCallback(async () => {
    setLoading(true);
    try {
      const { status, dateRange, searchText, domain } = filters;
      const { current, pageSize } = pagination;

      const dateFrom = dateRange?.[0]
        ? dateRange[0].startOf("day").toISOString()
        : undefined;
      const dateTo = dateRange?.[1]
        ? dateRange[1].endOf("day").toISOString()
        : undefined;

      const response = await getSentEmailsPaginated(id, {
        page: current,
        perPage: pageSize,
        status,
        dateFrom,
        dateTo,
        searchText: searchText?.trim(),
        domain: domain?.toLowerCase(),
        sortBy: "sentAt",
        sortOrder: "desc",
      });

      if (response.data.success) {
        const {
          sentEmails,
          stats,
          domainStats,
          pagination: paginationData,
          campaignName,
        } = response.data.data;
        setSentEmails(sentEmails);
        setSentEmailsStats(stats);
        setDomainStats(domainStats);
        setCampaignName(campaignName || "Campaign");
        setPagination({
          current: paginationData.currentPage,
          pageSize: paginationData.itemsPerPage,
          total: paginationData.totalItems,
        });
      }
    } catch (error) {
      console.error("Error fetching sent emails:", error);
    } finally {
      setLoading(false);
    }
  }, [id, filters, pagination.current, pagination.pageSize]);

  // Initial load
  useEffect(() => {
    fetchSentEmails();
  }, [fetchSentEmails]);

  // Debounced search handler
  const debouncedSearch = useCallback(
    debounce((value) => {
      setFilters((prev) => ({ ...prev, searchText: value }));
      setPagination((prev) => ({ ...prev, current: 1 }));
    }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Handle filter change
  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  // Handle pagination change
  const handleTableChange = (pagination, filters, sorter) => {
    // Update pagination
    setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
      total: pagination.total,
    });

    // Update filters based on table filters
    if (filters.status?.length) {
      handleFilterChange("status", filters.status[0]);
    }

    // Handle sort order
    if (sorter.field) {
      // You can implement server-side sorting here if needed
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      status: undefined,
      dateRange: undefined,
      searchText: "",
      domain: undefined,
    });
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  // Refresh data
  const handleRefresh = () => {
    fetchSentEmails();
  };

  const goBack = () => {
    const basePath =
      user.role === "superadmin" ? "/super-admin" : "/marketing-admin";
    history.push(`${basePath}/email/email-campaigns`);
  };

  // Get status tag class
  const getStatusClass = (status) => {
    if (status === "success") return "se-tag-success";
    if (status === "sent") return "se-tag-success";
    return "se-tag-error";
  };

  // Get status icon
  const getStatusIcon = (status) => {
    if (status === "success" || status === "sent")
      return <CheckCircleOutlined />;
    return <CloseCircleOutlined />;
  };

  // Extract domain from email
  const extractDomain = (email) => {
    if (!email) return "unknown";
    const parts = email.split("@");
    return parts.length > 1 ? parts[1].split(".")[0].toLowerCase() : "unknown";
  };

  // Get device icon
  const getDeviceIcon = (device) => {
    if (!device) return <InfoCircleOutlined />;
    return device.toLowerCase().includes("mobile") ? (
      <MobileOutlined />
    ) : (
      <DesktopOutlined />
    );
  };

  // Sent emails table columns
  const sentEmailsColumns = [
    {
      title: "Recipient",
      key: "recipient",
      render: (_, record) => {
        // Extract the domain from the email
        const domain = extractDomain(record.email);

        return (
          <Tooltip
            title={record.user ? record.user.email : record.email}
            className="se-tooltip"
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <Avatar
                style={{
                  backgroundColor: domainColors[domain] || domainColors.other,
                  marginRight: "8px",
                }}
                size="small"
              >
                {(
                  record.user?.name?.charAt(0) ||
                  record.email?.charAt(0) ||
                  "?"
                ).toUpperCase()}
              </Avatar>
              <div>
                <div style={{ fontWeight: "500" }}>
                  {record.user ? record.user.email : record.email}
                </div>
                <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
                  {record.sentFrom && <span>Sent from: {record.sentFrom}</span>}
                </div>
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "120px",
      render: (status) => (
        <Tag className={`se-tag ${getStatusClass(status)}`}>
          {getStatusIcon(status)}{" "}
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Tag>
      ),
      filters: [
        { text: "Success", value: "success" },
        { text: "Failed", value: "failed" },
      ],
    },
    {
      title: "Engagement",
      key: "engagement",
      width: "180px",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip
            title={
              <div className="se-tooltip-content">
                <div className="se-tooltip-title">Open Details</div>
                <p>
                  First Open:{" "}
                  {record.engagement?.firstOpenedAt
                    ? format(new Date(record.engagement.firstOpenedAt), "PPpp")
                    : "Never"}
                </p>
                <p>
                  Last Open:{" "}
                  {record.engagement?.lastOpenedAt
                    ? format(new Date(record.engagement.lastOpenedAt), "PPpp")
                    : "Never"}
                </p>
                <p>Total Opens: {record.engagement?.openCount || 0}</p>
              </div>
            }
            className="se-tooltip"
          >
            <Tag
              className={`se-tag ${
                record.engagement?.opened ? "se-tag-open" : ""
              }`}
            >
              <EyeOutlined /> {record.engagement?.openCount || 0}
            </Tag>
          </Tooltip>
          <Tooltip
            title={
              <div className="se-tooltip-content">
                <div className="se-tooltip-title">Click Details</div>
                <p>
                  First Click:{" "}
                  {record.engagement?.firstClickedAt
                    ? format(new Date(record.engagement.firstClickedAt), "PPpp")
                    : "Never"}
                </p>
                <p>
                  Last Click:{" "}
                  {record.engagement?.lastClickedAt
                    ? format(new Date(record.engagement.lastClickedAt), "PPpp")
                    : "Never"}
                </p>
                <p>
                  Unique Links: {record.engagement?.clickedLinks?.length || 0}
                </p>
              </div>
            }
            className="se-tooltip"
          >
            <Tag
              className={`se-tag ${
                record.engagement?.clicked ? "se-tag-click" : ""
              }`}
            >
              <LinkOutlined /> {record.engagement?.clickCount || 0}
            </Tag>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: "Device",
      key: "device",
      width: "140px",
      render: (_, record) => (
        <Tooltip
          title={
            <div className="se-tooltip-content">
              <div className="se-tooltip-title">Technical Details</div>
              <p>Client: {record.technical?.emailClient || "Unknown"}</p>
              <p>OS: {record.technical?.operatingSystem || "Unknown"}</p>
              <p>Device: {record.technical?.device || "Unknown"}</p>
            </div>
          }
          className="se-tooltip"
        >
          <Tag className="se-tag se-tag-device">
            {getDeviceIcon(record.technical?.device)}{" "}
            {record.technical?.device || "Unknown"}
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: "Domain",
      key: "domain",
      width: "120px",
      render: (_, record) => {
        const domain = extractDomain(record.email);
        return (
          <Tag
            style={{
              backgroundColor: `${
                domainColors[domain] || domainColors.other
              }22`,
              color: domainColors[domain] || domainColors.other,
              border: `1px solid ${domainColors[domain] || domainColors.other}`,
            }}
            className="se-tag"
          >
            <GlobalOutlined /> {domain}
          </Tag>
        );
      },
    },
    {
      title: "Sent At",
      dataIndex: "sentAt",
      key: "sentAt",
      width: "180px",
      render: (date) => (
        <span>
          <CalendarOutlined style={{ marginRight: "5px" }} />
          {format(new Date(date), "PPp")}
        </span>
      ),
      sorter: (a, b) => new Date(a.sentAt) - new Date(b.sentAt),
    },
    {
      title: "Error",
      dataIndex: "error",
      key: "error",
      width: "200px",
      render: (error) =>
        error ? (
          <Tooltip title={error} className="se-tooltip">
            <Tag
              className="se-tag se-tag-error"
              style={{
                maxWidth: "180px",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <WarningOutlined />{" "}
              {error.length > 20 ? `${error.substring(0, 20)}...` : error}
            </Tag>
          </Tooltip>
        ) : (
          "-"
        ),
    },
  ];

  return (
    <div className="se-container">
      {/* Header */}
      <div className="se-header">
        <Row justify="space-between" align="middle">
          <Col>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={goBack}
              style={{ marginRight: "10px" }}
            >
              Back to Campaigns
            </Button>
          </Col>
          <Col>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              className="se-btn"
              style={{
                marginRight: "10px",
                backgroundColor: "#fff",
                color: "rgba(0, 0, 0, 0.65)",
                border: "1px solid #d9d9d9",
                fontWeight: "500",
              }}
            >
              Refresh Data
            </Button>
            <Button
              icon={<PieChartOutlined />}
              onClick={() => {
                const basePath =
                  user.role === "superadmin"
                    ? "/super-admin"
                    : "/marketing-admin";
                history.push(
                  `${basePath}/email/email-campaigns/${id}/analytics`
                );
              }}
              className="se-btn"
              style={{
                backgroundColor: "#0050b3",
                color: "white",
                border: "none",
                fontWeight: "500",
                boxShadow: "0 2px 0 rgba(0, 0, 0, 0.045)",
              }}
            >
              View Analytics
            </Button>
          </Col>
        </Row>
      </div>

      <div style={{ padding: "0 24px" }}>
        {/* Title */}
        <div style={{ marginBottom: "24px" }}>
          <Title level={2} className="se-title">
            <MailOutlined style={{ marginRight: "10px", color: "#1890ff" }} />
            {campaignName}
          </Title>
          <Text type="secondary" className="se-subtitle">
            Viewing sent email statistics and engagement data
          </Text>
        </div>

        {/* Stats Section */}
        <Row gutter={16} style={{ marginBottom: "24px" }}>
          <Col xs={24} md={24} style={{ marginBottom: "12px" }}>
            <Card
              bordered={false}
              className="se-card"
              style={{ background: "#f9f9f9" }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <Text strong>Campaign ID:</Text>
                  <Text
                    copyable={{ text: id }}
                    style={{ marginLeft: "8px", fontSize: "16px" }}
                  >
                    {id}
                  </Text>
                </div>
                <Tag
                  color="blue"
                  style={{ fontWeight: "500", textTransform: "uppercase" }}
                >
                  CAMPAIGN IDENTIFIER
                </Tag>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card bordered={false} className="se-card se-stats-card">
              <div className="se-stats-value">
                {sentEmailsStats?.total || 0}
              </div>
              <div className="se-stats-title">Total Emails Sent</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              className="se-card se-stats-card se-stats-success"
            >
              <div className="se-stats-value" style={{ color: "#52c41a" }}>
                {(sentEmailsStats?.success || 0) + (sentEmailsStats?.sent || 0)}
              </div>
              <div className="se-stats-title">Successfully Delivered</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              className="se-card se-stats-card se-stats-failed"
            >
              <div className="se-stats-value" style={{ color: "#f5222d" }}>
                {sentEmailsStats?.failed || 0}
              </div>
              <div className="se-stats-title">Failed Deliveries</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              className="se-card se-stats-card se-stats-rate"
            >
              <div className="se-stats-value" style={{ color: "#faad14" }}>
                {sentEmailsStats?.successRate || 0}%
              </div>
              <div className="se-stats-title">Success Rate</div>
            </Card>
          </Col>
        </Row>

        {/* Domain Stats */}
        {domainStats && (
          <div style={{ marginBottom: "24px" }}>
            <h3 className="se-section-title">
              <PieChartOutlined style={{ marginRight: "8px" }} />
              Domain Statistics
            </h3>
            <Row gutter={16}>
              {Object.entries(domainStats).map(([domain, stats]) => (
                <Col xs={24} sm={12} md={8} lg={6} key={domain}>
                  <SentEmailsDomainCard domain={domain} stats={stats} />
                </Col>
              ))}
            </Row>
          </div>
        )}

        {/* Filters */}
        <div style={{ marginBottom: "24px" }}>
          <h3 className="se-section-title">
            <FilterOutlined style={{ marginRight: "8px" }} />
            Filter Results
          </h3>
          <Card bordered={false} className="se-card se-filters-card">
            <Row gutter={16} className="se-filter-row">
              <Col xs={24} sm={12} md={6}>
                <div style={{ marginBottom: "8px", fontWeight: "500" }}>
                  Status
                </div>
                <Select
                  placeholder="Filter by status"
                  style={{ width: "100%" }}
                  value={filters.status}
                  onChange={(value) => handleFilterChange("status", value)}
                  allowClear
                >
                  <Option value="success">Success</Option>
                  <Option value="failed">Failed</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div style={{ marginBottom: "8px", fontWeight: "500" }}>
                  Domain
                </div>
                <Select
                  placeholder="Filter by domain"
                  style={{ width: "100%" }}
                  value={filters.domain}
                  onChange={(value) => handleFilterChange("domain", value)}
                  allowClear
                >
                  {domainStats
                    ? Object.keys(domainStats).map((domain) => (
                        <Option key={domain} value={domain}>
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <div
                              style={{
                                width: "10px",
                                height: "10px",
                                borderRadius: "50%",
                                backgroundColor:
                                  domainColors[domain] || domainColors.other,
                                marginRight: "8px",
                              }}
                            />
                            {domain.charAt(0).toUpperCase() + domain.slice(1)}
                          </div>
                        </Option>
                      ))
                    : null}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div style={{ marginBottom: "8px", fontWeight: "500" }}>
                  Date Range
                </div>
                <RangePicker
                  style={{ width: "100%" }}
                  value={filters.dateRange}
                  onChange={(dates) => handleFilterChange("dateRange", dates)}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div style={{ marginBottom: "8px", fontWeight: "500" }}>
                  Search
                </div>
                <Search
                  placeholder="Search by email"
                  value={filters.searchText}
                  onChange={(e) => debouncedSearch(e.target.value)}
                  style={{ width: "100%" }}
                  allowClear
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <Button icon={<FilterOutlined />} onClick={clearFilters}>
                  Clear Filters
                </Button>
              </Col>
            </Row>
          </Card>
        </div>

        {/* Email Table */}
        <div style={{ marginBottom: "24px" }}>
          <h3 className="se-section-title">
            <LineChartOutlined style={{ marginRight: "8px" }} />
            Delivery Details
          </h3>
          <div className="se-table-container">
            <Table
              columns={sentEmailsColumns}
              dataSource={sentEmails}
              loading={loading}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `Total ${total} emails`,
              }}
              onChange={handleTableChange}
              rowKey="_id"
              scroll={{ x: "max-content", y: "calc(100vh - 500px)" }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No emails found"
                  />
                ),
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailCampaignSentEmails;
